import { useState } from 'react'
import Select from 'react-select'
import {
  Button,
  Col,
  Form,
  Row,
  Spinner,
} from 'react-bootstrap'
import {
  BarElement,
  Chart as ChartJS,
  Legend,
  LinearScale,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js'
import { skipToken } from '@tanstack/react-query'
import { useVisibleDevices } from '@data/device/device'
import Device, { DeviceIDTypes } from '@data/device/model/device'
import {
  ReportQuery,
  useReport30DaysQuery,
} from '@data/reports/report30Days'
import ReportDetail from './ReportDetail'
import { UOM_OPTIONS } from '../DeviceManagement/DeviceInformationManager/constants'
import { useCurrentUserQuery } from '@data/currentUser/currentUser'

import 'chartjs-adapter-moment'
import './30daysReport.scss'

ChartJS.register(LinearScale, BarElement, Title, Tooltip, Legend, PointElement, TimeScale)

// An empty numeric input parses to NaN, which Axios serialises as the literal string "NaN"
// rather than dropping it the way it drops undefined. Every one of these parameters is
// optional on the endpoint, so absent is the correct representation of a cleared field.
const parseOptionalInt = (value: string) => {
  const parsed = parseInt(value, 10)

  return Number.isNaN(parsed) ? undefined : parsed
}

// Everything ReportDetail renders comes from this snapshot, taken at submit. Passing it
// live form state instead is what let a generated report be shown — and exported, and
// emailed — against a device or a unit it was not generated for.
type SubmittedReport = {
  query: ReportQuery
  device: Device
}

export default function Report30Days() {
  const devices = useVisibleDevices()
  const { data: userInfo } = useCurrentUserQuery()

  const [submitted, setSubmitted] = useState<SubmittedReport>()

  const {
    data: report30days,
    isFetching,
    error,
  } = useReport30DaysQuery(submitted?.query ?? skipToken)

  const [selectedDevice, setSelectedDevice] = useState<Device>()
  const [offPeakStartHour, setOffPeakStartHour] = useState<number>()
  const [offPeakEndHour, setOffPeakEndHour] = useState<number>()
  const [offPeakBaseline, setOffPeakBaseline] = useState<number>()
  // Required by the endpoint, so it can be cleared in the form but not submitted empty.
  const [projectedSaving, setProjectedSaving] = useState<number | undefined>(30)
  const [selectedUom, setSelectedUom] = useState(userInfo?.preferences?.uom || 'Liters')

  // Submitting does not fetch; it commits the parameters, and the query reacts to them.
  const generateReport = () => {
    if (!selectedDevice || projectedSaving === undefined) return

    setSubmitted({
      device: selectedDevice,
      query: {
        deviceId: selectedDevice.dUuid,
        offPeakBaseline,
        projectedSaving,
        offPeakStartHour,
        offPeakEndHour,
        uom: selectedUom,
        idType: DeviceIDTypes.dUuid,
      },
    })
  }

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    generateReport()
  }

  const handleSelectedDevice = (device: Device) => {
    setSelectedDevice(device)
  }

  return (
    <div className="mt-5 ml-3">
      <h1>
        <span className="fas fa-dollar-sign mr-3"></span>Water Wastage Report
      </h1>
      <div className="container-fluid flex-container ">
        <div className="multiselect-container w-25">
          <Select
            classNamePrefix="select"
            className="basic-multi-select"
            value={selectedDevice}
            onChange={(device: any) => handleSelectedDevice(device)}
            options={devices}
            name="device"
            getOptionValue={(option) => option.deviceId}
            getOptionLabel={(option) => `${option.deviceName}`}
            isClearable={false}
            placeholder="Select a device..."
          />
        </div>
        <Form className="device-information-form w-25" onSubmit={handleSubmit}>
          <Row className="mb-3 mt-3">
            <Form.Group as={Col} controlId="off-peak-start">
              <Form.Label>Off Peak Start Hour</Form.Label>
              <Form.Control
                type="number"
                min={0}
                max={23}
                value={offPeakStartHour}
                onChange={(v) => setOffPeakStartHour(parseOptionalInt(v.target.value))}
              />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="off-peak-stop">
              <Form.Label>Off Peak End Hour</Form.Label>
              <Form.Control
                autoComplete="off"
                type="number"
                min={0}
                max={23}
                value={offPeakEndHour}
                onChange={(v) => setOffPeakEndHour(parseOptionalInt(v.target.value))}
              />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="expected-percentage">
              <Form.Label>Expected Baseline</Form.Label>
              <Form.Control
                autoComplete="off"
                type="number"
                value={offPeakBaseline}
                onChange={(v) => setOffPeakBaseline(parseOptionalInt(v.target.value))}
              />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="expected-percentage-savings">
              <Form.Label>Expected Percentage Saving</Form.Label>
              <Form.Control
                autoComplete="off"
                type="number"
                min={0}
                max={100}
                value={projectedSaving}
                onChange={(v) => setProjectedSaving(parseOptionalInt(v.target.value))}
              />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="expected-percentage-savings">
              <Form.Label>Unit of measure</Form.Label>
              <Select
                classNamePrefix="select"
                value={{
                  value: selectedUom,
                  label: selectedUom,
                }}
                onChange={(e: any) => setSelectedUom(e.value)}
                options={UOM_OPTIONS.map((uom) => {
                  return {
                    value: uom,
                    label:
                      uom === selectedDevice?.deviceSettings.uom
                        ? `${uom} (device default)`
                        : uom === userInfo?.preferences?.uom
                          ? `${uom} (your set preference)`
                          : uom,
                  }
                })}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => `${option.label}`}
                isClearable={false}
              />
            </Form.Group>
          </Row>

          <Button
            variant={'primary'}
            type={'submit'}
            className={'mb-3'}
            disabled={isFetching || projectedSaving === undefined}
          >
            {isFetching ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span>Loading...</span>
              </>
            ) : (
              <>Generate Report</>
            )}
          </Button>
        </Form>
        {selectedDevice ? (
          <div className="rtf-view-container">
            {error ? (
              <div className="report-error">
                Could not generate the report. Please try again.
              </div>
            ) : report30days && submitted ? (
              <div>
                <ReportDetail
                  device={submitted.device}
                  data={report30days}
                />
              </div>
            ) : null}
          </div>
        ) : (
          <div className="no-device-message">Select a device</div>
        )}
      </div>
    </div>
  )
}
